import { z } from "zod";

/**
 * Branded primitive types for richrich.
 *
 * These are the load-bearing value types the whole system agrees on. They are
 * `.brand()`ed so that a raw `string` can never be passed where a validated
 * `Decimal`/`Currency`/`MarketSymbol` is required — nominal typing catches the
 * "I forgot to parse this" class of bug at compile time.
 */

/**
 * Canonical money / quantity / price representation: a fixed-point **decimal
 * string** (never a JS float). Reproduces Toss `BigDecimal` exactly; all
 * arithmetic goes through `@richrich/core/money`, never native number math.
 *
 * Accepts an optional leading `-`, integer part, and optional fractional part.
 * No exponent notation, no thousands separators, no leading `+`.
 */
export const DecimalSchema = z
  .string()
  .regex(/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/, "must be a canonical decimal string")
  .brand<"Decimal">();
export type Decimal = z.infer<typeof DecimalSchema>;

/**
 * ISO-4217 currency. richrich trades only KRW/USD today (report base = KRW);
 * kept as a closed enum so an unknown currency fails closed rather than
 * silently flowing through money math.
 */
export const CurrencySchema = z.enum(["KRW", "USD"]).brand<"Currency">();
export type Currency = z.infer<typeof CurrencySchema>;

/**
 * Market symbol: KRX 6-digit numeric code (e.g. `005930`) or a US ticker
 * (e.g. `AAPL`, `BRK.B`). Bounded and character-restricted so it is safe to
 * embed in idempotency keys and `clientOrderId`s.
 */
export const MarketSymbolSchema = z
  .string()
  .trim()
  .min(1)
  .max(12)
  .regex(/^[A-Za-z0-9.-]+$/, "must be a KRX code or US ticker")
  .brand<"MarketSymbol">();
export type MarketSymbol = z.infer<typeof MarketSymbolSchema>;
